import { Platform } from 'react-native';

import { adUnits } from '../constants';
import { track } from './analytics';

// Lazy-loaded ads module — same pattern as app/lib/ads.ts and components/
// AdBanner.tsx. babel-preset-expo inlines Platform.OS at build time, so on
// web this `if` becomes `if (false)` and the require() is dead-code
// eliminated before Metro bundles it. Putting the require inside a function
// body would NOT get this treatment — Metro still walks the static dependency
// and would try to pull react-native-google-mobile-ads into the web bundle,
// which breaks because the package imports react-native internals.
type AdsModule = typeof import('react-native-google-mobile-ads');
let ads: AdsModule | null = null;

if (Platform.OS !== 'web') {
  try {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    ads = require('react-native-google-mobile-ads');
  } catch (err) {
    if (__DEV__) console.warn('[interstitial] module unavailable:', err);
  }
}

// If the network is slow and the ad never finishes loading, don't block the
// caller forever — bail after this and just continue with whatever flow the
// caller is in (share sheet, etc.).
const LOAD_TIMEOUT_MS = 8000;

/**
 * Attempts to show a full-screen interstitial. Resolves when the ad closes,
 * when it errors out, or after a safety timeout — so the caller can always
 * await it without risking a stuck flow. Frequency capping is delegated to
 * the AdMob console (per-ad-unit settings), not enforced here.
 *
 * Skips silently when:
 *   - running on web
 *   - the user disabled ads via the dev toggle
 *   - the native ads SDK is unavailable
 */
export async function maybeShowInterstitial(hideAds: boolean): Promise<void> {
  if (hideAds || !ads) return;
  const sdk = ads;

  return new Promise<void>((resolve) => {
    let resolved = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const finish = () => {
      if (resolved) return;
      resolved = true;
      if (timer) clearTimeout(timer);
      resolve();
    };

    try {
      const ad = sdk.InterstitialAd.createForAdRequest(adUnits.interstitial, {
        requestNonPersonalizedAdsOnly: false,
      });
      ad.addAdEventListener(sdk.AdEventType.LOADED, () => {
        ad.show().catch(() => finish());
      });
      ad.addAdEventListener(sdk.AdEventType.OPENED, () => {
        track('interstitial_shown');
      });
      ad.addAdEventListener(sdk.AdEventType.CLOSED, finish);
      ad.addAdEventListener(sdk.AdEventType.ERROR, (err: Error) => {
        track('interstitial_failed', { error: err?.message ?? 'unknown' });
        finish();
      });
      ad.load();
      timer = setTimeout(finish, LOAD_TIMEOUT_MS);
    } catch {
      finish();
    }
  });
}

export default { maybeShowInterstitial };
